import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { UnifiedInput } from "@/components/ui/customs/unified-input";
import { CustomRegionButton } from "@/components/backOffice/parameters/customRegionButton";
import { useNavigate } from "react-router-dom";
import Paths from "@/constants/paths";
import { CustomTable } from "@/components/backOffice/parameters/customTable";
import { Button } from "@/components/ui/button";
import { Plus, Search } from "lucide-react";
import { useAtomValue } from "jotai";
import { userAtom } from "@/stores/authStore";
import { ParametersHeader } from "@/components/shared/parametersHeader";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import { useRegionCustomOptions } from "@/hooks/useEnumOptions";
import { useTranslation } from "react-i18next";

export const CustomsParameterPage = () => {
  const { t } = useTranslation(["boCustoms", "common"]);
  const regionCustomOptions = useRegionCustomOptions();
  const customQuotationOnlyOptions = [
    { label: t("common:boolean.yes"), value: "true" },
    { label: t("common:boolean.no"), value: "false" },
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useAtomValue(userAtom);

  // Initialize state from URL parameters
  const [searchField, setSearchField] = useState<string>(searchParams.get("search") || "");
  const [selectedRegion, setSelectedRegion] = useState<number>(
    Number(searchParams.get("region")) || 0,
  );
  const [customQuotationOnly, setCustomQuotationOnly] = useState<string>(
    searchParams.get("customQuotationOnly") || "",
  );

  // Update URL when search field changes
  const handleSearchChange = useCallback(
    (value: React.SetStateAction<string>) => {
      const newValue = typeof value === "function" ? value(searchField) : value;
      setSearchField(newValue);
      const newSearchParams = new URLSearchParams(searchParams);
      if (newValue) {
        newSearchParams.set("search", newValue);
      } else {
        newSearchParams.delete("search");
      }
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams, searchField],
  );

  // Update URL when region changes
  const handleRegionChange = useCallback(
    (index: number) => {
      setSelectedRegion(index);
      const newSearchParams = new URLSearchParams(searchParams);
      if (index === 0) {
        newSearchParams.delete("region");
      } else {
        newSearchParams.set("region", index.toString());
      }
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const handleCustomQuotationOnlyChange = useCallback(
    (value: string) => {
      setCustomQuotationOnly(value);
      const newSearchParams = new URLSearchParams(searchParams);
      if (value) {
        newSearchParams.set("customQuotationOnly", value);
      } else {
        newSearchParams.delete("customQuotationOnly");
      }
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  // Sync state with URL parameters on mount
  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    const urlRegion = Number(searchParams.get("region")) || 0;
    const urlCustomQuotationOnly = searchParams.get("customQuotationOnly") || "";

    if (urlSearch !== searchField) {
      setSearchField(urlSearch);
    }
    if (urlRegion !== selectedRegion) {
      setSelectedRegion(urlRegion);
    }
    if (urlCustomQuotationOnly !== customQuotationOnly) {
      setCustomQuotationOnly(urlCustomQuotationOnly);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <ParametersHeader>
      <div className="flex h-full w-full flex-col gap-6 lg:px-8">
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold">{t("parametersPage.title")}</h2>
          <p className="text-sm text-gray-600">{t("parametersPage.subtitle")}</p>{" "}
        </div>
        <div className="flex w-full flex-row items-center justify-between">
          <UnifiedInput
            inputType="text"
            leftElement={<Search />}
            value={searchField}
            onChange={(value) => handleSearchChange(String(value ?? ""))}
            placeholder={t("parametersPage.searchPlaceholder")}
            wrapperClassName="w-full max-w-[400px] !h-10"
          />
          {user?.role === "SUPERADMIN" && (
            <Button
              variant="default"
              onClick={() => navigate(Paths.bo.customs, { state: { fromParameters: true } })}
            >
              <Plus className="size-4 text-white" />
              <span className="font-semibold text-white">{t("parametersPage.add")}</span>
            </Button>
          )}
        </div>
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex w-fit flex-row gap-1 rounded-lg border border-gray-200 bg-white p-0.5">
            {[{ label: t("parametersPage.allRegions"), value: "All" }, ...regionCustomOptions].map(
              (region, index) => (
                <CustomRegionButton
                  key={index}
                  selected={selectedRegion === index}
                  text={region.label}
                  onClick={() => handleRegionChange(index)}
                />
              ),
            )}
          </div>
          {/** Display this filter only for region "Autre" */}
          {selectedRegion === 9 && (
            <SelectV2
              placeholder={t("parametersPage.customQuotationOnlyPlaceholder")}
              clearable
              value={customQuotationOnly}
              options={customQuotationOnlyOptions}
              onChange={handleCustomQuotationOnlyChange}
              wrapperClassName="min-w-[280px]"
            />
          )}
        </div>
        <CustomTable
          selectedRegion={selectedRegion}
          searchField={searchField}
          customQuotationOnly={customQuotationOnly}
        />
      </div>
    </ParametersHeader>
  );
};
